import {
  Component,
  Input,
  OnInit,
  computed,
  inject,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { DialogModule } from 'primeng/dialog'; // cspell:disable-line
import { ButtonModule } from 'primeng/button'; // cspell:disable-line
import { SelectButtonModule } from 'primeng/selectbutton'; // cspell:disable-line
import { TooltipModule } from 'primeng/tooltip'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { ImpactService } from '../../services/impact.service';
import { ReductionTip, TipType } from '../../models/reduction-tip';
import { isSelectableTip } from '../../utils/reduction-tip-savings';
import {
  ObjectiveTip,
  ReductionObjectiveCardComponent,
} from './reduction-objective-card/reduction-objective-card.component';
import {
  ReductionTipGroupComponent,
  TipGroup,
} from './reduction-tip-group/reduction-tip-group.component';
import { ReductionTipsSummaryComponent } from './reduction-tips-summary/reduction-tips-summary.component';
import { ReductionTipsService } from '../../services/reduction-tips.service';

@Component({
  selector: 'app-reduction-tips',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    DialogModule,
    ButtonModule,
    SelectButtonModule,
    TooltipModule,
    LucideAngularModule,
    ReductionObjectiveCardComponent,
    ReductionTipGroupComponent,
    ReductionTipsSummaryComponent,
  ],
  templateUrl: './reduction-tips.component.html',
})
export class ReductionTipsComponent implements OnInit {
  @Input() tripId = '';
  tripMetrics = input<{ impactPerTraveller: number } | null>(null);

  private currentUserService = inject(CurrentUserService);
  /** Owns `viewMode`: the dialog toggle drives the whole impact page. */
  protected impactService = inject(ImpactService);
  private translocoService = inject(TranslocoService);
  protected reductionTipsService = inject(ReductionTipsService);

  tips = this.reductionTipsService.tips;
  commitments = this.reductionTipsService.commitments;
  isLoading = this.reductionTipsService.isLoading;

  viewModeOptions = [
    {
      label: this.translocoService.translate('impact.viewMode.group'),
      value: 'group',
    },
    {
      label: this.translocoService.translate('impact.viewMode.individual'),
      value: 'individual',
    },
  ];

  objectiveTips = computed<ObjectiveTip[]>(() => {
    const metrics = this.tripMetrics();
    if (!metrics) return [];
    return this.currentUserService
      .objectives()
      .filter((o) => o.isActive && o.type === 'AVG_EMISSION_PER_TRAVELLER')
      .map((o) => ({
        targetValue: o.targetValue,
        tripValue: metrics.impactPerTraveller,
        isAboveTarget: metrics.impactPerTraveller > o.targetValue,
        differencePercent: Math.abs(
          Math.round(
            ((metrics.impactPerTraveller - o.targetValue) / o.targetValue) *
              100,
          ),
        ),
      }));
  });

  /** Committed actions this trip already honours — nothing to improve on them. */
  respectedCommitments = computed(() =>
    this.commitments().filter((c) => c.status === 'respected'),
  );

  /**
   * Committed actions this trip falls short of. They already appear inside the
   * dialog as tips carrying the "your commitment" badge, but the whole point of
   * a commitment is that it should not need opening the dialog to be noticed —
   * so the trigger button gets an alert icon and a warning badge. The button
   * itself stays green: a full warning-coloured button read as an error.
   */
  unmetCommitments = computed(() =>
    this.commitments().filter((c) => c.status === 'improvable'),
  );

  hasUnmetCommitments = computed(() => this.unmetCommitments().length > 0);

  hasTips = computed(
    () =>
      this.tips().length > 0 ||
      this.objectiveTips().length > 0 ||
      this.respectedCommitments().length > 0,
  );

  /**
   * The API emits one tip per trip element, so the same advice repeats for
   * every flight or hotel. Grouping by type keeps the list short; actionable
   * groups come first, informational ones last.
   */
  tipGroups = computed<TipGroup[]>(() => {
    const byType = new Map<TipType, ReductionTip[]>();
    this.tips().forEach((tip) => {
      const existing = byType.get(tip.type);
      if (existing) existing.push(tip);
      else byType.set(tip.type, [tip]);
    });

    const groups = [...byType.entries()].map(([type, tips]) => ({
      type,
      tips,
    }));
    return [
      ...groups.filter((group) => isSelectableTip(group.tips[0])),
      ...groups.filter((group) => !isSelectableTip(group.tips[0])),
    ];
  });

  ngOnInit() {
    this.reductionTipsService.load(this.tripId);
  }
}
